import {
  type DependencyList,
  isValidElement,
  type JSXElementConstructor,
  type ReactNode,
  type RefCallback,
  type RefObject,
  type TransitionStartFunction,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
  useSyncExternalStore,
} from "react"

import { isElementInView } from "./DOM"

declare module "react" {
  // Allows any CSS Variable.
  interface CSSProperties {
    [key: `--${string}`]: string | number
  }

  // The current react type definition for forwardRef does not respect generic prop types.
  function forwardRef<T, P = object>(
    render: (props: P, ref: React.ForwardedRef<T>) => React.ReactElement | null,
  ): (props: P & React.RefAttributes<T>) => React.ReactElement | null
}

/**
 * Listens for matches to a CSS media query.
 * @param query A string specifying the media query to handle.
 * @returns A boolean value that returns `true` if the document currently
 * matches the media query, or `false` if not.
 */
export function useMediaQuery(query: string): boolean {
  const subscribe = useCallback(
    (onStoreChange: () => void) => {
      const mediaQueryList = matchMedia(query)
      mediaQueryList.addEventListener("change", onStoreChange)
      return () => {
        mediaQueryList.removeEventListener("change", onStoreChange)
      }
    },
    [query],
  )
  return useSyncExternalStore(subscribe, () => matchMedia(query).matches)
}

// Better/Easier alternative to React's useCallback.
// Note that this only works if the callback is called as a DOM Event listener; don't use this if the callback can be synchronously called in render().
// 1) https://blog.thoughtspile.tech/2021/04/07/better-usecallback/
// 2) https://github.com/radix-ui/primitives/blob/main/packages/react/use-callback-ref/src/useCallbackRef.tsx
// 3) https://github.com/reactjs/rfcs/pull/220
// eslint-disable-next-line @typescript-eslint/no-explicit-any
export function useStableCallback<T extends (...args: any[]) => any>(
  callback: T | undefined,
): T {
  const callbackRef = useRef(callback)

  useLayoutEffect(() => {
    callbackRef.current = callback
  })

  // eslint-disable-next-line @typescript-eslint/no-unsafe-return, @typescript-eslint/no-unsafe-argument
  return useMemo(() => ((...args) => callbackRef.current?.(...args)) as T, [])
}

export function useForceUpdate() {
  const [_, setState] = useState({})
  return useCallback(() => setState({}), [])
}

/*
 * Invokes a callback when the passed value hasn't changed for ms milliseconds.
 * For the simplest cases, the callback could just be a setState function.
 * The callback must be stable (i.e. not change between renders).
 */
export function useDebounce<T>(
  value: T,
  ms: number,
  stableCallback: (value: T) => void,
): void {
  const hasChanged = useRef(false)

  useOnChange(value, () => {
    hasChanged.current = true
  })

  useEffect(() => {
    // Skip the first render: We're only interested in live changes to the value.
    if (!hasChanged.current) return

    hasChanged.current = false

    const handler = setTimeout(() => stableCallback(value), ms)
    return () => clearTimeout(handler)
  }, [stableCallback, ms, value])
}

/**
 * Use this when you want to synchronize a local state when a prop changes.
 * Do not use this with a callback that has a bigger effect than just changing a local state or you will get a React warning.
 * useEffect is often used instead of a ref/state but it causes the component and all its children to be rendered twice:
 * once with the wrong combination of props and state and then once more with the final combination.
 * The callback is passed the new value for convenience, so that a state setter can be used as-is as a callback.
 * https://react.dev/learn/you-might-not-need-an-effect#adjusting-some-state-when-a-prop-changes
 */
export function useOnChange<A>(
  value: A,
  callback: (value: A, previousValue: A) => void,
) {
  const [previousValue, setPreviousValue] = useState(value)

  if (previousValue !== value) {
    setPreviousValue(value)
    callback(value, previousValue)
  }
}

interface UnSynchronizedUseDebounceStateArgs<T> {
  ms?: number
  initialState: T
  startTransition?: TransitionStartFunction
  synchronize?: never
}

interface SynchronizedUseDebounceStateArgs<T> {
  ms?: number
  state: T
  startTransition?: TransitionStartFunction
  synchronize: true
}

type UseDebounceStateArgs<T> =
  | UnSynchronizedUseDebounceStateArgs<T>
  | SynchronizedUseDebounceStateArgs<T>

/**
 * Use this to get and set a debounced state.
 * @param args.ms debounce time in milliseconds as number
 * @param args.initialState initial state of the debounce
 * @param args.startTransition optional React startTransition function
 * @param args.synchronize whether to synchronize the state with the initialState when it changes (default: false)
 * @returns [debounceState, setDebounceState]
 */
export function useDebounceState<T>(args: UseDebounceStateArgs<T>) {
  const { ms = 300, startTransition = (fn) => fn(), synchronize = false } = args

  const propsState = args.synchronize ? args.state : args.initialState

  const [state, setState] = useState(propsState)
  const [debouncedState, setDebouncedState] = useState(propsState)
  useDebounce(
    state,
    ms,
    useStableCallback(() => startTransition(() => setDebouncedState(state))),
  )

  if (synchronize && state !== propsState) {
    setState(propsState)
  }

  return { debouncedValue: debouncedState, set: setState, value: state }
}

interface UseInViewOptions {
  once?: boolean
}

/**
 * @param options.once whether we should stop observing after the first time the ref became visible.
 * @returns whether the ref is partially visible in the viewport.
 */
export function useInView(
  ref: RefObject<Element | null>,
  options: UseInViewOptions = {},
): boolean {
  const { once = false } = options

  const [inView, setInView] = useState(false)

  // useEffect is too late, we want to compute the correct initial/mount state before the first paint.
  useLayoutEffect(() => {
    if (!ref.current) return

    const mountedInView = isElementInView(ref.current)
    if (mountedInView) setInView(true)

    // Note that an IntersectionObserver callback is never called synchronously.
    const observer = new IntersectionObserver((entries) => {
      const isInView = entries[0]?.isIntersecting ?? false
      setInView(isInView)
      once && isInView && observer.disconnect()
    })

    observer.observe(ref.current)
    return () => observer.disconnect()
  }, [ref, once])

  return inView
}

type PossibleRef<T> = React.Ref<T> | undefined

/**
 * Set a given ref to a given value
 * This utility takes care of different types of refs: callback refs and RefObject(s)
 */
function setRef<T>(ref: PossibleRef<T>, value: T) {
  if (typeof ref === "function") {
    ref(value)
  } else if (ref !== null && ref !== undefined) {
    ;(ref as React.MutableRefObject<T>).current = value
  }
}

/**
 * A utility to compose multiple refs together
 * Accepts callback refs and RefObject(s)
 */
export function composeRefs<T>(...refs: PossibleRef<T>[]) {
  return (node: T | null) => refs.forEach((ref) => setRef(ref, node))
}

/**
 * A custom hook that composes multiple refs
 * Accepts callback refs and RefObject(s)
 */
export function useComposedRefs<T>(...refs: PossibleRef<T>[]) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  return useCallback(composeRefs(...refs), refs)
}

export function useTabFocus(onFocus: () => void) {
  useEffect(() => {
    function doRevalidateOnFocus() {
      if (document.visibilityState === "hidden") return

      onFocus()
    }

    document.addEventListener("visibilitychange", doRevalidateOnFocus)

    return () =>
      document.removeEventListener("visibilitychange", doRevalidateOnFocus)
  })
}

export function refContainsTarget(
  ref: RefObject<Node>,
  target: EventTarget | null,
) {
  if (!ref.current) return false
  if (!(target instanceof Node)) return false

  return ref.current.contains(target)
}

/**
 * A custom hook that creates an interval callback that is stable between renders
 * @param callback - the callback to be invoked on each interval
 * @param delay - the delay between each interval
 * @returns void
 *
 * based on Dan Abramovs useInterval hook [Making setInterval Declarative with React Hooks](https://overreacted.io/making-setinterval-declarative-with-react-hooks/)
 */
export function useInterval(callback: () => void, delay: number | null) {
  const stableCallback = useStableCallback(callback)

  // Set up the interval.
  useEffect(() => {
    function tick() {
      stableCallback()
    }
    if (delay !== null) {
      const id = setInterval(tick, delay)
      return () => clearInterval(id)
    }

    return () => {}
  }, [delay, stableCallback])
}

/**
 * A custom hook that triggers a callback based on whether a click occurs
 * outside of the passed ref
 */
export function useClickedOutside(
  ref: RefObject<Node> | RefObject<Node>[],
  onClickOutside: (event: MouseEvent) => void,
) {
  const onStableClickOutside = useStableCallback(onClickOutside)
  const getRef = useStableCallback(() => ref)

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      const target = event.target as Node

      // Do nothing if the target is not connected element with document
      if (!target || !target.isConnected) {
        return
      }

      const elementRef = getRef()

      const isOutside = Array.isArray(elementRef)
        ? elementRef
            .filter((r) => Boolean(r.current))
            .every((r) => r.current && !r.current.contains(target))
        : elementRef.current && !elementRef.current.contains(target)

      if (isOutside) {
        onStableClickOutside(event)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => {
      document.removeEventListener("mousedown", handleClickOutside)
    }
  }, [getRef, onStableClickOutside])
}

/**
 * A function that validates a component is of type T
 */
// eslint-disable-next-line @typescript-eslint/no-explicit-any -- type needs to be wide enough
export function isComponentOfType<T extends JSXElementConstructor<any>>(
  node: ReactNode,
  type: T,
) {
  return isValidElement(node) && node.type === type
}

/**
 * A custom hook that listens for the size of an element
 * TODO: Performance to be improved in PBD-1815
 */
export function useElementSize<E extends Element>(
  callback: (size: ResizeObserverSize, element: E) => void,
  deps: DependencyList,
): RefCallback<E> {
  const resizeObserverRef = useRef<ResizeObserver>()
  return useCallback((element: E | null) => {
    resizeObserverRef.current?.disconnect()
    if (element === null) return
    resizeObserverRef.current = new ResizeObserver(([entry]) => {
      if (!entry) return
      const { height, width } = entry.contentRect
      callback({ blockSize: height, inlineSize: width }, element)
    })
    resizeObserverRef.current.observe(element)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps)
}

/**
 * Simple hook that updates a ref on every render with the value passed in, and returns a getter for it.
 */
export function useRefGetter<T>(value: T) {
  const ref = useRef(value)

  useEffect(() => {
    ref.current = value
  }, [value])

  return () => ref.current
}

/**
 * Drop-in replacement for React 19's `use` hook.
 * Note that this only works if the Promise reference is stable across renders
 * (and not stored inside a react ref/state in the same Component as those get destroyed when suspending)
 * Gotten from https://github.com/liveblocks/liveblocks/blob/main/packages/liveblocks-react/src/lib/use-polyfill.ts#L6
 */
export const use = <T>(
  promise: Promise<T> & {
    status?: "pending" | "fulfilled" | "rejected"
    value?: T
    reason?: unknown
  },
): T => {
  if (promise.status === "pending") {
    throw promise
  } else if (promise.status === "fulfilled") {
    return promise.value as T
  } else if (promise.status === "rejected") {
    throw promise.reason
  } else {
    promise.status = "pending"
    promise.then(
      (v) => {
        promise.status = "fulfilled"
        promise.value = v
      },
      (e) => {
        promise.status = "rejected"
        promise.reason = e
      },
    )
    throw promise
  }
}

interface UseHasBeenIdleOptions {
  idleTimeSeconds: number
}

export function useHasBeenIdle({ idleTimeSeconds }: UseHasBeenIdleOptions) {
  const [hasBeenIdle, setHasBeenIdle] = useState(false)
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null)

  const clearTimer = useCallback(() => {
    if (timerRef.current) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
  }, [])

  const startTimer = useCallback(() => {
    clearTimer()
    timerRef.current = setTimeout(() => {
      setHasBeenIdle(true)
    }, idleTimeSeconds * 1000)
  }, [clearTimer, idleTimeSeconds])

  useEffect(() => {
    if (hasBeenIdle) {
      clearTimer()
      return
    }

    const reset = () => {
      clearTimer()
      startTimer()
    }

    const events = ["click", "scroll", "keydown", "mousemove"]
    events.forEach((evt) => window.addEventListener(evt, reset))
    startTimer()

    return () => {
      clearTimer()
      events.forEach((evt) => window.removeEventListener(evt, reset))
    }
  }, [hasBeenIdle, clearTimer, startTimer])

  return { hasBeenIdle }
}
